import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MainLayout } from './components/layout/MainLayout';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ContactsPage from './pages/ContactsPage';
import ContactDetailPage from './pages/ContactDetailPage';
import OpportunitiesPage from './pages/OpportunitiesPage';
import QuotesPage from './pages/QuotesPage';
import InvoicesPage from './pages/InvoicesPage';
import UsersPage from './pages/UsersPage';
import AuditLogPage from './pages/AuditLogPage';
import ProductsPage from './pages/ProductsPage';
import TicketsPage from './pages/TicketsPage';
import SettingsPage from './pages/SettingsPage';
import './App.css';

// ── Route guards ──────────────────────────────────────────────────────────────

function ProtectedRoute({ children, adminOnly = false }) {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) return <LoadingScreen dark />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />;

  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  // Pendant le retour de redirection Keycloak, AuthContext tient `loading` à
  // vrai le temps de l'échange : l'écran de chargement couvre le callback.
  if (loading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return children;
}

function LoadingScreen({ dark = false }) {
  return (
    <div className={`min-h-screen flex items-center justify-center ${dark ? 'bg-slate-50' : 'bg-slate-900'}`}>
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
        {dark && <p className="mt-4 text-slate-600">Chargement…</p>}
      </div>
    </div>
  );
}

// ── Routes ────────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Routes>
      {/* Root: login page (or SSO callback landing) */}
      <Route path="/" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />

      {/* Protected app routes */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard"    element={<DashboardPage />} />
        <Route path="/contacts"     element={<ContactsPage />} />
        <Route path="/contacts/:id" element={<ContactDetailPage />} />
        <Route path="/opportunities" element={<OpportunitiesPage />} />
        <Route path="/products"     element={<ProductsPage />} />
        <Route path="/tickets"      element={<TicketsPage />} />
        <Route path="/quotes"       element={<QuotesPage />} />
        <Route path="/invoices"     element={<InvoicesPage />} />
        {/* MainLayout pointait vers /settings sans route déclarée : le
            catch-all renvoyait sur /dashboard. */}
        <Route path="/settings"     element={<SettingsPage />} />

        {/* Admin only */}
        <Route path="/users" element={
          <ProtectedRoute adminOnly>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="/audit-logs" element={
          <ProtectedRoute adminOnly>
            <AuditLogPage />
          </ProtectedRoute>
        } />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
